import { useQuery } from '@tanstack/react-query';
import type { Building } from '@map/shared';
import { metalOf, type Metal } from '../globe/Medals.js';
import { fetchBookReview } from './api.js';

const MEDAL: Record<Metal, { name: string; colour: string }> = {
  bronze: { name: 'Bronze', colour: 'var(--medal-bronze)' },
  silver: { name: 'Silver', colour: 'var(--medal-silver)' },
  gold: { name: 'Gold', colour: 'var(--medal-gold)' },
  bright: { name: 'Shining gold', colour: 'var(--medal-bright)' },
};

/** "2026-10-02" → "2 Oct 2026"; a month-only "2022-06" → "Jun 2022". */
export function formatBookDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m) return date;
  const month = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' }).slice(0, 3);
  return d ? `${d} ${month} ${y}` : `${month} ${y}`;
}

/** A book read: its medal, when it was finished, and the review, fetched when the card opens. */
export function BookCard({ building }: { building: Building }) {
  const book = building.book;
  const review = useQuery({
    queryKey: ['book-review', building.id],
    queryFn: () => fetchBookReview(building.id),
    staleTime: 5 * 60_000,
  });
  if (!book) return null;
  const medal = MEDAL[metalOf(book.rating)];
  return (
    <div className="flex flex-col gap-3 text-sm">
      <header>
        <p className="text-base font-medium">{building.name}</p>
        {book.author && <p className="text-text-muted">{book.author}</p>}
        <p className="mt-1 flex items-center gap-2">
          <span className="inline-block h-3 w-3 shrink-0 rounded-full" style={{ background: medal.colour }} aria-hidden />
          <span aria-label={`${book.rating} of 5 stars`} style={{ color: medal.colour }}>
            {'★'.repeat(book.rating)}
            <span className="text-text-muted">{'☆'.repeat(5 - book.rating)}</span>
          </span>
          <span className="text-text-muted">{medal.name} medal</span>
        </p>
        <p className="text-xs text-text-muted">
          {book.readAt ? `Read ${formatBookDate(book.readAt)}` : `Added ${formatBookDate(book.addedAt)} · no read date`}
        </p>
      </header>
      {review.isPending ? (
        <p className="text-text-muted">Loading the review…</p>
      ) : review.isError ? (
        <p className="text-text-muted">Could not load the review.</p>
      ) : review.data.review ? (
        <p className="whitespace-pre-wrap leading-relaxed">{review.data.review}</p>
      ) : (
        <p className="text-text-muted">No review written.</p>
      )}
    </div>
  );
}
